import activityTxs from '@exodus/activity-txs';
import createIOC from '@exodus/argo';
import assetSources from '@exodus/asset-sources';
import assets from '@exodus/assets-feature';
import availableAssets from '@exodus/available-assets';
import balances from '@exodus/balances';
import { pick } from '@exodus/basic-utils';
import blockchainMetadata from '@exodus/blockchain-metadata';
import enabledAssets from '@exodus/enabled-assets';
import exchange from '@exodus/exchange';
import feeDataMonitors from '@exodus/fee-data-monitors';
import fees from '@exodus/fees';
import fiatBalances from '@exodus/fiat-balances';
import fiatRateConverter from '@exodus/fiat-rate-converter';
import fusion from '@exodus/fusion-local';
import geolocation from '@exodus/geolocation';
import locale from '@exodus/locale';
import marketHistory from '@exodus/market-history';
import nfts from '@exodus/nfts';
import pricing from '@exodus/pricing';
import rates from '@exodus/rates-monitor';
import remoteConfig from '@exodus/remote-config';
import restoreProgressTracker from '@exodus/restore-progress-tracker';
import syncTime from '@exodus/sync-time';
import txSimulator from '@exodus/tx-simulator';
import uiConfig from '@exodus/ui-config';
import walletAccounts from '@exodus/wallet-accounts';
import addressProvider from '@passkeys/address-provider';
import applicationFeature from '@passkeys/application';
import messageSigner from '@passkeys/message-signer';
import orders from '@passkeys/orders';
import publicKeyProvider from '@passkeys/public-key-provider';
import transactionSigner from '@passkeys/transaction-signer';
import transactions from '@passkeys/transactions';
import txLogMonitors from '@passkeys/tx-log-monitors';
import wallet from '@passkeys/wallet-feature';
import createApi from './api.js';
import attachAtoms from './atoms/attach.js';
import createDependencies from './dependencies/index.js';
import { attachApiResolver } from './ioc.js';
import attachPlugins from './plugins/attach.js';
import stubs from './stubs.js';
const createSdk = (dependencies = {}) => {
    const adapters = { ...dependencies.adapters };
    const config = { ...dependencies.config };
    const ioc = createIOC({ adapters, config })
        .use(addressProvider(config.addressProvider))
        .use(applicationFeature())
        .use(assets())
        .use(assetSources())
        .use(blockchainMetadata())
        .use(exchange(config.exchange))
        .use(feeDataMonitors())
        .use(locale())
        .use(messageSigner())
        .use(pricing())
        .use(publicKeyProvider())
        .use(rates())
        .use(remoteConfig(config.remoteConfig))
        .use(transactions())
        .use(transactionSigner())
        .use(txSimulator())
        .use(orders(config.orders))
        .use(uiConfig({ config: config.uiConfig }))
        .use(wallet())
        .use(walletAccounts())
        // The below features are shipped with outdated factory types.
        .use(availableAssets(config.availableAssets))
        .use(enabledAssets(config.enabledAssets))
        // The below features are not typed and need a type assertion.
        .use(activityTxs())
        .use(balances())
        .use(fees())
        .use(fiatBalances({ optimistic: false }))
        .use(fiatRateConverter({ defaultCurrency: 'USD' }))
        .use(fusion())
        .use(geolocation())
        .use(marketHistory())
        .use(nfts({ fetchCollectionStats: false }))
        .use(restoreProgressTracker())
        .use(syncTime())
        .use(txLogMonitors())
        // Non-feature dependencies.
        .registerMultiple(createDependencies({ adapters, config }))
        .registerMultiple(stubs);
    const resolve = () => {
        ioc.resolve();
        const { application } = ioc.getByType('module');
        const port = ioc.get('port');
        application.on('start', (payload) => port.emit('start', payload));
        application.on('load', (payload) => port.emit('load', payload));
        application.on('create', (payload) => port.emit('start', payload));
        application.on('unlock', () => port.emit('unlock'));
        application.on('lock', () => port.emit('lock'));
        application.on('restore', () => port.emit('restore'));
        application.on('restore-completed', () => port.emit('restore-completed'));
        application.on('restart', (payload) => port.emit('restart', payload));
        attachAtoms({
            port,
            application,
            atoms: pick(ioc.getByType('atom'), Object.keys(dependencies.eventsReduxMap ?? {})),
        });
        attachPlugins({
            application,
            plugins: ioc.getByType('plugin'),
            logger: ioc.get('createLogger')('attachPlugins'),
        });
        return createApi({ ioc, port });
    };
    return attachApiResolver({
        ioc,
        methods: ['use', 'register', 'registerMultiple'],
        resolve,
    });
};
export default createSdk;
